import { useCallback, useState } from "react";
import type {
  SeasonalEvent,
  SeasonalEventPrompt,
} from "~/integrations/firebase/collections/seasonal-events";
import { SearchQuery, SearchType } from "~/hooks/search/types";
import { buildCatalogPayload } from "~/pages/books/analytics/build-catalog-payload";
import { useCatalogAnalytics } from "~/pages/books/analytics/use-catalog-analytics";

interface UseSeasonalEventModalArgs {
  searchQuery: SearchQuery;
  setSearchQuery: (query: Partial<SearchQuery>) => void;
  clearSelection: () => void;
}

interface UseSeasonalEventModalResult {
  // Retained through the close animation, so it lags activeEvent back to null.
  renderedEvent: SeasonalEvent | null;
  isOpen: boolean;
  openSeasonalEventModal: (event: SeasonalEvent) => void;
  onOpenChange: (open: boolean) => void;
  onRunSearch: (
    prompt: SeasonalEventPrompt,
    refreshIndex: number,
    eventId: string,
  ) => void;
}

export const useSeasonalEventModal = ({
  searchQuery,
  setSearchQuery,
  clearSelection,
}: UseSeasonalEventModalArgs): UseSeasonalEventModalResult => {
  const { registerPromptSearchIntent } = useCatalogAnalytics();
  const [activeEvent, setActiveEvent] = useState<SeasonalEvent | null>(null);
  const [renderedEvent, setRenderedEvent] = useState<SeasonalEvent | null>(
    null,
  );

  const openSeasonalEventModal = useCallback((event: SeasonalEvent) => {
    setRenderedEvent(event);
    setActiveEvent(event);
  }, []);

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setActiveEvent(null);
    }
  }, []);

  const onRunSearch = useCallback(
    (
      prompt: SeasonalEventPrompt,
      refreshIndex: number,
      // The modal can switch to another event via its calendar view, so the
      // opened-with activeEvent is not necessarily the prompt's event.
      eventId: string,
    ) => {
      if (!activeEvent) return;

      const promptText = prompt.text.trim();
      setActiveEvent(null);

      setSearchQuery({
        query: promptText,
        type: SearchType.SEMANTIC,
        page: null,
        filters: {},
      });
      clearSelection();

      // Compare against the same normalized request shape the search key uses
      // so page=1 vs null and whitespace-only differences do not leave a dangling intent.
      const isUnchangedSearch =
        searchQuery.query.trim() === promptText &&
        searchQuery.type === SearchType.SEMANTIC &&
        (searchQuery.page ?? 1) === 1 &&
        !buildCatalogPayload(searchQuery).has_filters;
      if (isUnchangedSearch) return;

      registerPromptSearchIntent({
        source: "seasonal",
        eventId,
        promptId: prompt.prompt_id,
        refreshIndex,
        targetQuery: promptText,
        targetSearchType: SearchType.SEMANTIC,
        targetPage: 1,
      });
    },
    [
      activeEvent,
      clearSelection,
      registerPromptSearchIntent,
      searchQuery,
      setSearchQuery,
    ],
  );

  return {
    renderedEvent,
    isOpen: activeEvent !== null,
    openSeasonalEventModal,
    onOpenChange,
    onRunSearch,
  };
};
